import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import MissingSelectOption from '../components/MissingSelectOption';
import { validateLiveContractForm, hasErrors, formatErrors } from '../utils/validation';

const HIRE_CHARGE_PERIODS = [
  '5WEEK',
  '6WEEK',
  '7WEEK',
  'HOUR',
  'SC',
  'W/E',
  'YEAR',
  'MONTH',
  'DAY',
  '5DAY',
  '6DAY',
  '7DAY',
];

const LIVE_CONTRACT_FIELD_TABS = {
  contract_no: 'contract',
  start_date: 'contract',
  account_no: 'contract',
  order_no: 'contract',
  contact_name: 'contract',
  depot: 'contract',
  site_name: 'site',
  line_1: 'site',
  line_2: 'site',
  town: 'site',
  county: 'site',
  postcode: 'site',
  cat: 'line',
  stock_no: 'line',
  description: 'line',
  quantity: 'line',
  charge_rate: 'line',
  charge_period: 'line',
  discount_pct: 'line',
  orig_start_date: 'line',
  orig_start_time: 'line',
  off_hire_date: 'line',
  off_hire_time: 'line',
  notes: 'line',
  planned_end: 'line',
};

function emptyForm() {
  return {
    contract_no: '',
    account_no: '',
    site_name: '',
    line_1: '',
    line_2: '',
    town: '',
    county: '',
    postcode: '',
    start_date: '',
    order_no: '',
    contact_name: '',
    depot: '',
    cat: '',
    stock_no: '',
    description: '',
    quantity: '',
    charge_rate: '',
    charge_period: '',
    discount_pct: '',
    orig_start_date: '',
    orig_start_time: '',
    off_hire_date: '',
    off_hire_time: '',
    notes: '',
    planned_end: '',
  };
}

function toForm(record) {
  if (!record) {
    return emptyForm();
  }
  const dateField = (value) => (value ? String(value).split('T')[0] : '');
  return {
    contract_no: record.contract_no || '',
    account_no: record.account_no || '',
    site_name: record.site_name || '',
    line_1: record.line_1 || '',
    line_2: record.line_2 || '',
    town: record.town || '',
    county: record.county || '',
    postcode: record.postcode || '',
    start_date: dateField(record.start_date),
    order_no: record.order_no || '',
    contact_name: record.contact_name || '',
    depot: record.depot || '',
    cat: record.cat || '',
    stock_no: record.stock_no || '',
    description: record.description || '',
    quantity: record.quantity ?? '',
    charge_rate: record.charge_rate ?? '',
    charge_period: record.charge_period || '',
    discount_pct: record.discount_pct ?? '',
    orig_start_date: dateField(record.orig_start_date),
    orig_start_time: record.orig_start_time || '',
    off_hire_date: dateField(record.off_hire_date),
    off_hire_time: record.off_hire_time || '',
    notes: record.notes || '',
    planned_end: dateField(record.planned_end),
  };
}

function buildSavePayload(formData) {
  const payload = { ...formData };
  for (const key of ['quantity', 'charge_rate', 'discount_pct']) {
    if (payload[key] === '') {
      payload[key] = null;
    }
  }
  for (const key of [
    'line_1',
    'line_2',
    'town',
    'county',
    'postcode',
    'order_no',
    'contact_name',
    'depot',
    'stock_no',
    'charge_period',
    'orig_start_time',
    'off_hire_time',
    'notes',
    'orig_start_date',
    'off_hire_date',
    'planned_end',
  ]) {
    if (payload[key] === '') {
      payload[key] = null;
    }
  }
  return payload;
}

function buildCopyPayload(record) {
  const payload = buildSavePayload(toForm(record));
  const match = String(payload.contract_no || '').match(/^(.*?)(\d+)$/);
  if (match) {
    const base = match[1];
    const pad = match[2].length;
    const num = parseInt(match[2], 10) + 1;
    payload.contract_no = `${base}${String(num).padStart(pad, '0')}`;
  } else if (payload.contract_no) {
    payload.contract_no = `${payload.contract_no}-COPY`;
  }
  return payload;
}

function LiveContractModal({ record, onClose, onSave }) {
  const [formData, setFormData] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [depots, setDepots] = useState([]);
  const [hireCategories, setHireCategories] = useState([]);
  const [salesCategories, setSalesCategories] = useState([]);
  const [stockNumbers, setStockNumbers] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('contract');

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [customersRes, depotsRes, hireCatsRes, salesCatsRes, stockRes] = await Promise.all([
          axios.get('/api/customers', { params: { page: 1, limit: 10000 } }),
          axios.get('/api/depots', { params: { page: 1, limit: 1000 } }),
          axios.get('/api/hire-categories/codes'),
          axios.get('/api/sales-categories', { params: { page: 1, limit: 10000 } }),
          axios.get('/api/hire-stock/stock-numbers'),
        ]);
        setCustomers(customersRes.data.customers || []);
        setDepots(depotsRes.data.depots || depotsRes.data.items || []);
        setHireCategories(Array.isArray(hireCatsRes.data) ? hireCatsRes.data : []);
        setSalesCategories(salesCatsRes.data.salesCategories || salesCatsRes.data.items || []);
        setStockNumbers(stockRes.data || []);
      } catch (error) {
        console.error('Failed to fetch lookup data:', error);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    setFormData(toForm(record));
    setSaveError('');
    setValidationErrors({});
    setActiveTab('contract');
  }, [record]);

  useEffect(() => {
    const account = formData.account_no?.trim();
    if (!account) {
      setSites([]);
      return undefined;
    }
    let active = true;
    axios
      .get('/api/customer-sites', { params: { customer: account, page: 1, limit: 10000 } })
      .then((response) => {
        if (active) {
          setSites(response.data.customerSites || []);
        }
      })
      .catch(() => {
        if (active) {
          setSites([]);
        }
      });
    return () => {
      active = false;
    };
  }, [formData.account_no]);

  const categoryOptions = useMemo(() => {
    const hire = hireCategories.map((cat) => ({
      code: cat.code || cat,
      label: typeof cat === 'string' ? cat : `${cat.code} - ${cat.name || ''}`,
      type: 'hire',
    }));
    const sales = salesCategories.map((cat) => ({
      code: cat.code,
      label: `${cat.code} - ${cat.name || ''}`,
      type: 'sales',
    }));
    const seen = new Set();
    return [...hire, ...sales].filter((item) => {
      if (!item.code || seen.has(item.code)) {
        return false;
      }
      seen.add(item.code);
      return true;
    });
  }, [hireCategories, salesCategories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleSiteSelect = (e) => {
    const siteName = e.target.value;
    const site = sites.find((s) => s.site_name === siteName);
    setFormData((prev) => ({
      ...prev,
      site_name: siteName,
      line_1: site?.line_1 || prev.line_1,
      line_2: site?.line_2 || prev.line_2,
      town: site?.town || prev.town,
      county: site?.county || prev.county,
      postcode: site?.postcode || prev.postcode,
    }));
    setSaveError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateLiveContractForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      const firstField = Object.keys(errors)[0];
      if (firstField && LIVE_CONTRACT_FIELD_TABS[firstField]) {
        setActiveTab(LIVE_CONTRACT_FIELD_TABS[firstField]);
      }
      return;
    }
    onSave(buildSavePayload(formData));
  };

  const renderFieldError = (name) =>
    validationErrors[name] ? (
      <Form.Text className="text-danger d-block">{validationErrors[name]}</Form.Text>
    ) : null;

  return (
    <Modal show={true} onHide={onClose} size="lg" style={{ zIndex: 1060 }}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{record ? 'Edit Live Contract' : 'Add Live Contract'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}
          <Tabs activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)} className="mb-3">
            <Tab eventKey="contract" title="Contract">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contract No *</Form.Label>
                    <Form.Control
                      name="contract_no"
                      value={formData.contract_no}
                      onChange={handleChange}
                      maxLength={20}
                      required
                    />
                    {renderFieldError('contract_no')}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                    />
                    {renderFieldError('start_date')}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account No *</Form.Label>
                    <Form.Select
                      name="account_no"
                      value={formData.account_no}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select customer...</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.account_no}>
                          {customer.account_no} - {customer.name}
                        </option>
                      ))}
                    </Form.Select>
                    <MissingSelectOption
                      value={formData.account_no}
                      options={customers}
                      getOptionValue={(customer) => customer.account_no}
                    />
                    {renderFieldError('account_no')}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order No</Form.Label>
                    <Form.Control
                      name="order_no"
                      value={formData.order_no}
                      onChange={handleChange}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contact Name</Form.Label>
                    <Form.Control
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Depot</Form.Label>
                    <Form.Select name="depot" value={formData.depot} onChange={handleChange}>
                      <option value="">Select depot...</option>
                      {depots.map((depot) => (
                        <option key={depot.id || depot.short_code} value={depot.short_code}>
                          {depot.short_code} - {depot.name}
                        </option>
                      ))}
                    </Form.Select>
                    {renderFieldError('depot')}
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="site" title="Site">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Site Name *</Form.Label>
                    <Form.Select value={formData.site_name} onChange={handleSiteSelect} required>
                      <option value="">Select site...</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.site_name}>
                          {site.site_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      className="mt-2"
                      name="site_name"
                      value={formData.site_name}
                      onChange={handleChange}
                      placeholder="Or enter site name"
                      maxLength={50}
                      required
                    />
                    {renderFieldError('site_name')}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      maxLength={8}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Line 1</Form.Label>
                    <Form.Control
                      name="line_1"
                      value={formData.line_1}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Line 2</Form.Label>
                    <Form.Control
                      name="line_2"
                      value={formData.line_2}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Town</Form.Label>
                    <Form.Control
                      name="town"
                      value={formData.town}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>County</Form.Label>
                    <Form.Control
                      name="county"
                      value={formData.county}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="line" title="Line & Charges">
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cat *</Form.Label>
                    <Form.Select name="cat" value={formData.cat} onChange={handleChange} required>
                      <option value="">Select category...</option>
                      {categoryOptions.map((cat) => (
                        <option key={cat.code} value={cat.code}>
                          {cat.label}
                        </option>
                      ))}
                    </Form.Select>
                    {renderFieldError('cat')}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stock No</Form.Label>
                    <Form.Select name="stock_no" value={formData.stock_no} onChange={handleChange}>
                      <option value="">Select stock...</option>
                      {stockNumbers.map((item) => (
                        <option key={item.stock_no} value={item.stock_no}>
                          {item.stock_no} - {item.name}
                        </option>
                      ))}
                    </Form.Select>
                    {renderFieldError('stock_no')}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.01"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                    />
                    {renderFieldError('quantity')}
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  maxLength={128}
                  required
                />
                {renderFieldError('description')}
              </Form.Group>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Charge Rate</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      name="charge_rate"
                      value={formData.charge_rate}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Charge Period</Form.Label>
                    <Form.Select
                      name="charge_period"
                      value={formData.charge_period}
                      onChange={handleChange}
                    >
                      <option value="">Select period...</option>
                      {HIRE_CHARGE_PERIODS.map((period) => (
                        <option key={period} value={period}>
                          {period}
                        </option>
                      ))}
                    </Form.Select>
                    {renderFieldError('charge_period')}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Discount %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="1"
                      max="99"
                      name="discount_pct"
                      value={formData.discount_pct}
                      onChange={handleChange}
                    />
                    {renderFieldError('discount_pct')}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Orig Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="orig_start_date"
                      value={formData.orig_start_date}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Orig Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="orig_start_time"
                      value={formData.orig_start_time}
                      onChange={handleChange}
                    />
                    {renderFieldError('orig_start_time')}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Off Hire Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="off_hire_date"
                      value={formData.off_hire_date}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Off Hire Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="off_hire_time"
                      value={formData.off_hire_time}
                      onChange={handleChange}
                    />
                    {renderFieldError('off_hire_time')}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Planned End</Form.Label>
                    <Form.Control
                      type="date"
                      name="planned_end"
                      value={formData.planned_end}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  maxLength={512}
                />
              </Form.Group>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {record ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function LiveContracts() {
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();

  const fetchRecords = async (page = pagination.page, search = searchTerm) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/live-contracts', {
        params: { page, limit: pagination.limit, search },
      });
      setRecords(response.data.liveContracts || []);
      setPagination(response.data.pagination || pagination);
      setError('');
    } catch {
      setError('Failed to fetch live contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(1, searchTerm);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await axios.put(`/api/live-contracts/${editing.id}`, payload);
        setSuccess('Live contract updated successfully');
      } else {
        await axios.post('/api/live-contracts', payload);
        setSuccess('Live contract created successfully');
      }
      setShowModal(false);
      setEditing(null);
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save live contract');
    }
  };

  const handleCopy = async (record) => {
    setCopyingId(record.id);
    setError('');
    try {
      await wrapCopy('Copying live contract...', 1, async (tick) => {
        await axios.post('/api/live-contracts', buildCopyPayload(record));
        tick({ success: true });
      });
      setSuccess('Live contract copied successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy live contract');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this live contract?')) {
      return;
    }
    try {
      await axios.delete(`/api/live-contracts/${id}`);
      setSuccess('Live contract deleted successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch {
      setError('Failed to delete live contract');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL live contracts?')) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all live contract records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/live-contracts');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      fetchRecords(1, '');
    } catch {
      setError('Failed to delete all records');
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString();
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i
                className="fas fa-file-contract me-3 text-primary"
                style={{ fontSize: '2.5rem' }}
              ></i>
              <div>
                <h1 className="mb-0">Live Contracts</h1>
                <p className="text-muted mb-0">Manage active hire contract lines</p>
              </div>
            </div>
            <div>
              <Button variant="primary" onClick={handleAdd} className="me-2">
                Add Contract
              </Button>
              <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {copyProgress && (
        <OperationProgress
          title={copyProgress.title}
          processed={copyProgress.processed}
          total={copyProgress.total}
          success={copyProgress.success}
          failed={copyProgress.failed}
          variant="info"
        />
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by contract no, account, site, stock no, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="p-4">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center p-4 text-muted">No live contracts found.</div>
          ) : (
            <>
              <div className="table-responsive scrollable-grid">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Contract No</th>
                      <th>Account</th>
                      <th>Site</th>
                      <th>Start Date</th>
                      <th>Cat</th>
                      <th>Stock No</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Period</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row) => (
                      <tr key={row.id}>
                        <td>{row.contract_no}</td>
                        <td>{row.account_no}</td>
                        <td>{row.site_name}</td>
                        <td>{formatDate(row.start_date)}</td>
                        <td>{row.cat}</td>
                        <td>{row.stock_no || '-'}</td>
                        <td>{row.description}</td>
                        <td>{row.quantity ?? '-'}</td>
                        <td>{row.charge_rate ?? '-'}</td>
                        <td>{row.charge_period || '-'}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEdit(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleCopy(row)}
                            disabled={copyingId === row.id}
                          >
                            {copyingId === row.id ? 'Copying...' : 'Copy'}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(row.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <GridPagination
                pagination={pagination}
                onPageChange={(page) => fetchRecords(page, searchTerm)}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <LiveContractModal
          record={editing}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
    </Container>
  );
}

export default LiveContracts;
